/**
 * Mitgeltende Dokumente an Arbeitsschritten ergänzen — reine Logik, ohne
 * Datenbank und ohne Oberfläche.
 *
 * Grundsätze:
 *  - Es wird nur ERGÄNZT, nie entfernt. Was ein Nutzer an einem Schritt
 *    eingetragen hat, bleibt in Wortlaut und Reihenfolge stehen; neue
 *    Dokumente werden hinten angehängt (`ergaenzeDokumente`).
 *  - Vorschläge mit Bedingung („nur im Bestand“, „bei Wandhöhe über 5 m“)
 *    werden nie von selbst übernommen: ob die Bedingung zutrifft, weiß nur
 *    der Bearbeiter. Sie werden angeboten, aber nicht vorbelegt bzw. nicht
 *    vorausgewählt.
 *  - Wer eine Spalte schreibt, legt auch nur DIESE Spalte auf den lokalen
 *    Stand — nie eine ganze (womöglich ältere) Antwortzeile. Sonst setzte die
 *    Antwort der Ergänzung eine inzwischen gespeicherte Maßnahme zurück, und
 *    umgekehrt.
 *  - Alle Schreibvorgänge auf einen bestehenden Arbeitsschritt laufen über
 *    die Schreib-Warteschlange (`schrittSchreibWarteschlange.ts`): je Schritt
 *    strikt nacheinander, und jeder Auftrag liest die FRISCHE Zeile und
 *    berechnet seine Payload erst bei Ausführung (Detailansicht: Dreiwege-
 *    Abgleich gegen die Basis; Ergänzung: Hinzufügen zum gelesenen Stand;
 *    Dialog: nur geänderte Spalten). So endet die Datenbank in jeder
 *    Reihenfolge bei der Vereinigung.
 *
 * Genutzt von
 *  - `MitgeltendeDokumenteEditor` (Vorschläge zu einem Schritt),
 *  - `ArbeitsschritteListe` (Vorbelegung im Dialog „Neuer Arbeitsschritt“
 *    und der plan-weite Knopf „Unterlagen ergänzen“),
 *  - `SchrittDetail` (nur geänderte Listen speichern, Abgleich mit Änderungen
 *    von außen).
 * Die Vorbelegung neuer Zeilen beim Muster-Import/Übernehmen liegt in
 * `uebernahme.ts` (`mitgeltendeFuerNeueZeile`).
 */
import {
  ergaenzeDokumente,
  findeDokument,
  normalisiere,
  schlageDokumenteVor,
} from '../../services/mitgeltendeDokumente';
import type { DokumentVorschlag, SchrittText } from '../../services/mitgeltendeDokumente';
import type { Arbeitsschritt } from './types';
import { reiheSchrittAuftrag } from './schrittSchreibWarteschlange';

/** Die Felder eines Schritts, aus denen Vorschläge berechnet werden. */
export interface SchrittQuelle {
  bezeichnung: string;
  gewerk?: string | null;
  schutzmassnahmen?: string[] | null;
  vorschriften?: string[] | null;
  mitgeltende_dokumente?: string[] | null;
}

/**
 * Vergleichsschlüssel eines Dokumentnamens: Katalog-Id, wenn der Katalog das
 * Dokument kennt (Langform, Kurzform und Alias zählen dann als dasselbe
 * Dokument), sonst der normalisierte freie Text.
 */
export const dokumentSchluessel = (name: string): string =>
  findeDokument(name)?.id ?? normalisiere(name);

/** Gleiche Einträge in gleicher Reihenfolge. */
export const gleicheListe = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((x, i) => x === b[i]);

/** Gilt der Vorschlag nur unter einer Bedingung? Dann nie von selbst übernehmen. */
export const istBedingt = (v: DokumentVorschlag): boolean => v.bedingungen.length > 0;

/**
 * Eingabe für den Vorschlag: bereits eingetragene Dokumente UND die
 * Vorschriften gelten als vorhanden — steht etwa die Baustellenordnung schon
 * unter den Vorschriften, wird sie nicht noch einmal vorgeschlagen.
 */
export const vorschlagsEingabe = (s: SchrittQuelle): SchrittText => ({
  bezeichnung: s.bezeichnung || '',
  gewerk: s.gewerk || '',
  schutzmassnahmen: s.schutzmassnahmen || [],
  vorhanden: [...(s.mitgeltende_dokumente || []), ...(s.vorschriften || [])],
});

/** Vorschläge zu einem Schritt, ohne das, was dort schon steht. */
export const vorschlaegeFuerSchritt = (s: SchrittQuelle): DokumentVorschlag[] =>
  s.bezeichnung?.trim() || s.gewerk?.trim() ? schlageDokumenteVor(vorschlagsEingabe(s)) : [];

// ─── Vorbelegung im Dialog „Neuer Arbeitsschritt“ ──────────────────────────

/**
 * Namen, die beim Anlegen eines Schritts automatisch vorbelegt werden: nur
 * die BEDINGUNGSLOSEN Vorschläge. Bedingte bietet der Editor weiter an.
 *
 * Bewusst OHNE die schon eingetragenen Dokumente als `vorhanden` gerechnet —
 * sonst sähe jeder vorbelegte Eintrag in der nächsten Runde wie „nicht mehr
 * vorgeschlagen“ aus (siehe `aktualisiereVorbelegung`). Die Vorschriften
 * zählen dagegen als vorhanden.
 */
export const vorbelegungsNamen = (s: Omit<SchrittQuelle, 'mitgeltende_dokumente'>): string[] =>
  vorschlaegeFuerSchritt({ ...s, mitgeltende_dokumente: [] })
    .filter(v => !istBedingt(v))
    .map(v => v.dokument.name);

export interface Vorbelegung {
  /** Die Dokumentliste, wie sie im Formular stehen soll. */
  dokumente: string[];
  /** Davon automatisch vorbelegt (und vom Nutzer nicht angefasst). */
  automatisch: string[];
}

/**
 * Hält die automatische Vorbelegung aktuell, während Bezeichnung und Gewerk
 * getippt werden.
 *
 * - Von Hand (oder aus dem Katalog) eingetragene Dokumente bleiben immer.
 * - Automatisch vorbelegte, die nicht mehr vorgeschlagen werden, fallen
 *   wieder heraus — sonst sammelten sich beim Tippen Treffer aus
 *   Zwischenständen („Kran…“ → „Kranbahn“) an.
 * - Neue Vorschläge kommen hinzu, außer der Nutzer hat sie im Dialog schon
 *   einmal entfernt (`abgelehnt`).
 *
 * `vorschlaege` sind Namen aus `vorbelegungsNamen`.
 */
export const aktualisiereVorbelegung = (
  dokumente: string[],
  automatisch: string[],
  vorschlaege: string[],
  abgelehnt: string[],
): Vorbelegung => {
  const vorschlagSchluessel = new Set(vorschlaege.map(dokumentSchluessel));
  const autoSchluessel = new Set(automatisch.map(dokumentSchluessel));
  const abgelehntSchluessel = new Set(abgelehnt.map(dokumentSchluessel));

  const bleiben = dokumente.filter(d => {
    const k = dokumentSchluessel(d);
    return !autoSchluessel.has(k) || vorschlagSchluessel.has(k);
  });
  const bleibenSchluessel = new Set(bleiben.map(dokumentSchluessel));
  const neu = ergaenzeDokumente(
    bleiben,
    vorschlaege.filter(v => !abgelehntSchluessel.has(dokumentSchluessel(v))),
  );
  return {
    dokumente: neu,
    automatisch: neu.filter(d => {
      const k = dokumentSchluessel(d);
      return autoSchluessel.has(k) || !bleibenSchluessel.has(k);
    }),
  };
};

/**
 * Was der Nutzer gegenüber dem vorigen Stand entfernt hat — wird als
 * „abgelehnt“ gemerkt, damit die Vorbelegung es nicht wieder einträgt.
 */
export const entfernteDokumente = (vorher: string[], nachher: string[]): string[] => {
  const bleibt = new Set(nachher.map(dokumentSchluessel));
  return vorher.filter(d => !bleibt.has(dokumentSchluessel(d)));
};

// ─── Plan-weit: „Unterlagen ergänzen“ ──────────────────────────────────────

/** Ein Schritt, der durch die Ergänzung neue Dokumente bekäme. */
export interface ErgaenzungsPosten {
  schrittId: string;
  bezeichnung: string;
  hauptabschnitt?: string;
  /** Stand vor der Ergänzung. */
  vorhanden: string[];
  /** Alle Vorschläge, die hinzukämen (Reihenfolge des Katalogs) — auch bedingte. */
  neu: DokumentVorschlag[];
}

/**
 * Berechnet für alle Schritte des Plans, welche Dokumente hinzukämen.
 * Schritte ohne Ergänzung erscheinen nicht. Reihenfolge = Reihenfolge der
 * Schritte.
 */
export const berechneErgaenzungen = (schritte: Arbeitsschritt[]): ErgaenzungsPosten[] =>
  schritte.flatMap(s => {
    const vorhanden = s.mitgeltende_dokumente || [];
    const vorschlaege = vorschlaegeFuerSchritt(s);
    if (!vorschlaege.length) return [];
    const nachher = ergaenzeDokumente(vorhanden, vorschlaege.map(v => v.dokument.name));
    const hinzu = new Set(nachher.filter(n => !vorhanden.includes(n)));
    const neu = vorschlaege.filter(v => hinzu.has(v.dokument.name));
    return neu.length
      ? [{ schrittId: s.id, bezeichnung: s.bezeichnung, hauptabschnitt: s.hauptabschnitt, vorhanden, neu }]
      : [];
  });

/** Schlüssel einer einzelnen Auswahl (Schritt × Dokument) in der Vorschau. */
export const auswahlSchluessel = (schrittId: string, name: string): string => `${schrittId}\u0000${name}`;

/**
 * Anfangsauswahl der Vorschau: bedingte Vorschläge stehen da, sind aber NICHT
 * angehakt — der Bearbeiter muss die Bedingung bewusst bejahen.
 */
export const standardAbwahl = (posten: ErgaenzungsPosten[]): Set<string> =>
  new Set(posten.flatMap(p => p.neu.filter(istBedingt).map(v => auswahlSchluessel(p.schrittId, v.dokument.name))));

/** Ein Schreibauftrag für einen Schritt. */
export interface ErgaenzungsAuftrag {
  schrittId: string;
  bezeichnung: string;
  /** Neue vollständige Liste nach dem Stand beim Bestätigen (für Anzeige und Zählung). */
  dokumente: string[];
  /** Die hinzukommenden Namen — beim Schreiben erneut gegen den dann aktuellen Stand ergänzt. */
  hinzu: string[];
}

/**
 * Baut die Schreibaufträge aus der Vorschau.
 *
 * Gerechnet wird gegen den AKTUELLEN Stand der Schritte (`aktuell`), nicht
 * gegen den Stand beim Öffnen der Vorschau: hat das automatische Speichern
 * der Detailansicht inzwischen ein Dokument geschrieben, bleibt es erhalten.
 * Abgewählte Dokumente und gelöschte Schritte fallen heraus; Schritte, die
 * nichts mehr hinzubekämen, auch.
 */
export const baueAuftraege = (
  posten: ErgaenzungsPosten[],
  abgewaehlt: ReadonlySet<string>,
  aktuell: Arbeitsschritt[],
): ErgaenzungsAuftrag[] => {
  const nachId = new Map(aktuell.map(s => [s.id, s]));
  return posten.flatMap(p => {
    const schritt = nachId.get(p.schrittId);
    if (!schritt) return [];
    const gewaehlt = p.neu
      .map(v => v.dokument.name)
      .filter(n => !abgewaehlt.has(auswahlSchluessel(p.schrittId, n)));
    if (!gewaehlt.length) return [];
    const vorher = schritt.mitgeltende_dokumente || [];
    const dokumente = ergaenzeDokumente(vorher, gewaehlt);
    const hinzu = dokumente.filter(n => !vorher.includes(n));
    return hinzu.length ? [{ schrittId: p.schrittId, bezeichnung: schritt.bezeichnung, dokumente, hinzu }] : [];
  });
};

export interface ErgaenzungsFehler {
  schrittId: string;
  bezeichnung: string;
  meldung: string;
}

/** Was je Schritt tatsächlich gespeichert wurde — NUR die eine Spalte. */
export interface GeschriebeneDokumente {
  schrittId: string;
  mitgeltende_dokumente: string[];
  hinzu: string[];
}

export interface ErgaenzungsErgebnis {
  geschrieben: GeschriebeneDokumente[];
  fehler: ErgaenzungsFehler[];
  /** Summe der tatsächlich hinzugekommenen Dokumente. */
  dokumenteGeschrieben: number;
}

/** Datenbankzugriff eines Auftrags — in der App Supabase, im Test eine Attrappe. */
export interface SchrittDb {
  /** Frische Zeile lesen; `null`, wenn es den Schritt nicht mehr gibt. */
  lese: (schrittId: string) => Promise<Arbeitsschritt | null>;
  /** Nur die übergebenen Spalten schreiben; liefert die gespeicherte Zeile (oder wirft). */
  schreibe: (schrittId: string, felder: Partial<Arbeitsschritt>) => Promise<Arbeitsschritt | null>;
}

const fehlerText = (e: unknown): string =>
  e instanceof Error ? e.message : (e as { message?: string })?.message || String(e);

/**
 * Schreibt die Aufträge — je Schritt über die Schreib-Warteschlange, also
 * nie gleichzeitig mit dem Autospeichern der Detailansicht oder dem Dialog.
 *
 * Die Liste wird ERST BEI AUSFÜHRUNG berechnet: frische Zeile lesen, deren
 * Dokumente um das Hinzugefügte ergänzen, schreiben. Was die Detailansicht
 * vorher gespeichert hat, ist damit enthalten — ein Nachschreiben ist nicht
 * mehr nötig. Gelöschte Schritte und schon Vorhandenes entfallen still.
 *
 * Ein Fehler bricht nicht ab: er wird gesammelt, die übrigen Schritte werden
 * trotzdem ergänzt. Liefert das Schreiben keine Zeile, gilt das als Fehler
 * (die Datenbank hat nichts geändert, z. B. fehlende Berechtigung).
 */
export const schreibeErgaenzungen = async (
  auftraege: ErgaenzungsAuftrag[],
  db: SchrittDb,
  onFortschritt?: (erledigt: number, gesamt: number) => void,
): Promise<ErgaenzungsErgebnis> => {
  const geschrieben: GeschriebeneDokumente[] = [];
  const fehler: ErgaenzungsFehler[] = [];
  let dokumenteGeschrieben = 0;
  for (let i = 0; i < auftraege.length; i++) {
    const a = auftraege[i];
    try {
      const r = await reiheSchrittAuftrag(a.schrittId, async (): Promise<GeschriebeneDokumente | null> => {
        const zeile = await db.lese(a.schrittId);
        if (!zeile) return null;
        const vorher = zeile.mitgeltende_dokumente || [];
        const dokumente = ergaenzeDokumente(vorher, a.hinzu);
        const hinzu = dokumente.filter(n => !vorher.includes(n));
        if (!hinzu.length) return null;
        const antwort = await db.schreibe(a.schrittId, { mitgeltende_dokumente: dokumente });
        if (!antwort) throw new Error('Keine Zeile geändert (fehlende Berechtigung?)');
        return { schrittId: a.schrittId, mitgeltende_dokumente: antwort.mitgeltende_dokumente ?? dokumente, hinzu };
      });
      if (r) {
        geschrieben.push(r);
        dokumenteGeschrieben += r.hinzu.length;
      }
    } catch (e: unknown) {
      fehler.push({ schrittId: a.schrittId, bezeichnung: a.bezeichnung, meldung: fehlerText(e) });
    }
    onFortschritt?.(i + 1, auftraege.length);
  }
  return { geschrieben, fehler, dokumenteGeschrieben };
};

/**
 * Übernimmt das Ergebnis der Ergänzung in den AKTUELLEN lokalen Stand —
 * nur die Spalte `mitgeltende_dokumente`, alle anderen Felder bleiben, wie sie
 * jetzt sind (eine inzwischen gespeicherte Maßnahme bleibt stehen). Die
 * Dokumentliste wird nicht durch die Antwort ERSETZT, sondern um das
 * Hinzugefügte ERGÄNZT: eine inzwischen gemeldete neuere Fassung der
 * Detailansicht bleibt so stehen.
 */
export const fuehreErgaenzungZusammen = (schritte: Arbeitsschritt[], geschrieben: GeschriebeneDokumente[]): Arbeitsschritt[] => {
  if (!geschrieben.length) return schritte;
  const nachId = new Map(geschrieben.map(g => [g.schrittId, g]));
  return schritte.map(s => {
    const g = nachId.get(s.id);
    return g ? { ...s, mitgeltende_dokumente: ergaenzeDokumente(s.mitgeltende_dokumente || [], g.hinzu) } : s;
  });
};

/** Fehlermeldung für das Banner der Liste — nennt jeden betroffenen Schritt. */
export const fehlerMeldung = (fehler: ErgaenzungsFehler[]): string | null => {
  if (!fehler.length) return null;
  const einzeln = fehler.map(f => `„${f.bezeichnung}“ (${f.meldung})`).join('; ');
  return fehler.length === 1
    ? `Unterlagen ergänzen: 1 Schritt konnte nicht gespeichert werden: ${einzeln}`
    : `Unterlagen ergänzen: ${fehler.length} Schritte konnten nicht gespeichert werden: ${einzeln}`;
};

/**
 * Hinweistext zu einer Bedingung eines Vorschlags („Nur im Bestand …“).
 * Bringt die Bedingung ein „nur“ schon mit, bleibt sie, wie sie ist; beginnt
 * sie mit einer Präposition oder Konjunktion („bei …“, „im …“, „wenn …“),
 * wird „Nur“ vorangestellt; sonst „Nur wenn:“.
 */
export const bedingungsHinweis = (bedingung: string): string => {
  const b = bedingung.trim();
  if (!b) return '';
  const gross = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);
  if (/(^|\s)nur\s/i.test(b)) return gross(b);
  if (/^(bei|beim|im|in|an|am|auf|ab|wenn|falls|sofern|soweit|solange)\s/i.test(b)) return `Nur ${b}`;
  return `Nur wenn: ${b}`;
};

// ─── Detailansicht: nur geänderte Listen speichern ─────────────────────────

/** Die drei Listen, die die Detailansicht eines Schritts bearbeitet. */
export type ListenFeld = 'schutzmassnahmen' | 'vorschriften' | 'mitgeltende_dokumente';
export const LISTEN_FELDER: readonly ListenFeld[] = ['schutzmassnahmen', 'vorschriften', 'mitgeltende_dokumente'];
export type Listen = Record<ListenFeld, string[]>;

export const listenAus = (s: Pick<Arbeitsschritt, ListenFeld>): Listen => ({
  schutzmassnahmen: s.schutzmassnahmen || [],
  vorschriften: s.vorschriften || [],
  mitgeltende_dokumente: s.mitgeltende_dokumente || [],
});

/**
 * Dreiwege-Zusammenführung einer Liste: der Stand von außen ohne die lokal
 * entfernten Einträge, dahinter die lokal hinzugefügten (Reihenfolge bleibt,
 * Dubletten normalisiert über `ergaenzeDokumente`). „Lokal entfernt“ und
 * „lokal hinzugefügt“ gelten jeweils gegenüber der `basis`, dem zuletzt von
 * außen übernommenen Stand.
 */
export const dreiwegeListe = (basis: string[], lokal: string[], aussen: string[]): string[] => {
  const lokalSchluessel = new Set(lokal.map(dokumentSchluessel));
  const basisSchluessel = new Set(basis.map(dokumentSchluessel));
  const entfernt = new Set(basis.map(dokumentSchluessel).filter(k => !lokalSchluessel.has(k)));
  const hinzu = lokal.filter(l => !basisSchluessel.has(dokumentSchluessel(l)));
  return ergaenzeDokumente(aussen.filter(a => !entfernt.has(dokumentSchluessel(a))), hinzu);
};

export interface Abgleich {
  listen: Listen;
  basis: Listen;
  /** Lokal geänderte Listen, die mit einem Stand von außen zusammengeführt wurden — neu zu speichern. */
  zusammengefuehrt: ListenFeld[];
}

/**
 * Abgleich mit einem geänderten Stand von außen, je Liste:
 *  - nicht offen (lokal nichts Ungespeichertes) → der Stand von außen gilt;
 *  - offen, außen unverändert (gleich der Basis) → bleibt;
 *  - offen UND außen geändert → Dreiwege-Zusammenführung; das Ergebnis ist
 *    eine NEUE lokale Änderung und muss gespeichert werden (`zusammengefuehrt`).
 * Die Basis wird bei jeder Änderung von außen auf den Stand von außen gesetzt.
 */
export const abgleichDreiwege = (
  lokal: Listen,
  basis: Listen,
  aussen: Listen,
  offen: { has: (f: ListenFeld) => boolean },
): Abgleich => {
  const listen = { ...lokal };
  const neueBasis = { ...basis };
  const zusammengefuehrt: ListenFeld[] = [];
  LISTEN_FELDER.forEach(f => {
    if (!offen.has(f)) {
      listen[f] = aussen[f];
      neueBasis[f] = aussen[f];
    } else if (!gleicheListe(aussen[f], basis[f])) {
      listen[f] = dreiwegeListe(basis[f], lokal[f], aussen[f]);
      neueBasis[f] = aussen[f];
      zusammengefuehrt.push(f);
    }
  });
  return { listen, basis: neueBasis, zusammengefuehrt };
};

/**
 * Bearbeitungsstand der Detailansicht für EINEN Schritt. Ein Speicherauftrag
 * hält sich an genau dieses Objekt — wechselt die Ansicht den Schritt, schreibt
 * ein noch laufender Auftrag weiter den alten Schritt mit dessen Listen.
 *
 * Revisionen: je Liste ein Zähler, der bei jeder lokalen Änderung (auch einer
 * Zusammenführung) steigt und nie zurückgesetzt oder gelöscht wird, dazu die
 * höchste gespeicherte Revision. Offen ist eine Liste, solange
 * `rev > gespeichert`. Ein Auftrag merkt sich die gesendete Revision; danach
 * ist die Liste nur sauber, wenn seither keine neuere entstand.
 */
export interface DetailStand {
  readonly schrittId: string;
  lokal: Listen;
  /** Je Liste der zuletzt von außen bzw. aus der Datenbank übernommene Stand. */
  basis: Listen;
  rev: Record<ListenFeld, number>;
  gespeichert: Record<ListenFeld, number>;
}

const nullRevisionen = (): Record<ListenFeld, number> => ({ schutzmassnahmen: 0, vorschriften: 0, mitgeltende_dokumente: 0 });

export const neuerDetailStand = (s: Pick<Arbeitsschritt, 'id' | ListenFeld>): DetailStand => {
  const listen = listenAus(s);
  return { schrittId: s.id, lokal: listen, basis: listen, rev: nullRevisionen(), gespeichert: nullRevisionen() };
};

export const istOffen = (d: DetailStand, f: ListenFeld): boolean => d.rev[f] > d.gespeichert[f];
export const offeneFelder = (d: DetailStand): ListenFeld[] => LISTEN_FELDER.filter(f => istOffen(d, f));

/** Lokale Änderung einer Liste (Revision hoch). */
export const aendereDetail = (d: DetailStand, f: ListenFeld, items: string[]): void => {
  d.lokal = { ...d.lokal, [f]: items };
  d.rev[f] += 1;
};

/**
 * Dreiwege-Abgleich mit der FRISCHEN Zeile aus der Datenbank (nur im
 * Speicherauftrag, also hinter allen früheren Schreibvorgängen des Schritts).
 * Zusammengeführte Listen bekommen eine neue Revision und sind damit offen.
 *
 * Nur gegen die frische Zeile, nie gegen den Elternzustand: der kann einen
 * Moment ÄLTER sein als die Basis (etwa die Rückmeldung der Ergänzung, die
 * mit einem noch nicht gerenderten Stand rechnete). Eine Dreiwege-Zusammen-
 * führung gegen einen älteren „außen“-Stand hielte dessen Lücken für
 * Löschungen und verlöre Einträge.
 */
export const gleicheDetailAb = (d: DetailStand, aussen: Listen): ListenFeld[] => {
  const r = abgleichDreiwege(d.lokal, d.basis, aussen, { has: f => istOffen(d, f) });
  d.lokal = r.listen;
  d.basis = r.basis;
  r.zusammengefuehrt.forEach(f => { d.rev[f] += 1; });
  return r.zusammengefuehrt;
};

/**
 * Neuer Elternzustand (Rückmeldung der Liste, des Dialogs oder der eigenen
 * Speicherung):
 *  - Listen ohne offene Änderung übernehmen ihn (lokal und Basis);
 *  - offene Listen bleiben unverändert. Weicht der Stand dort von der Basis
 *    ab, wurde außen geschrieben — Rückgabe `true`: die Ansicht plant ein
 *    Speichern, und dessen Auftrag führt dreiwege gegen die frische Zeile
 *    zusammen (`speichereDetail`).
 */
export const uebernimmVonAussen = (d: DetailStand, aussen: Listen): boolean => {
  let speichern = false;
  LISTEN_FELDER.forEach(f => {
    if (!istOffen(d, f)) {
      d.lokal = { ...d.lokal, [f]: aussen[f] };
      d.basis = { ...d.basis, [f]: aussen[f] };
    } else if (!gleicheListe(aussen[f], d.basis[f])) {
      speichern = true;
    }
  });
  return speichern;
};

export interface DetailSpeicherErgebnis {
  /** Die drei Listen, wie sie nach dem Schreiben in der Datenbank stehen — nach oben zu melden. */
  zeile: Listen;
  /** Die ganze gespeicherte Zeile. */
  antwort: Arbeitsschritt;
  /** Beim Ausführen mit der frischen Zeile zusammengeführte Listen. */
  zusammengefuehrt: ListenFeld[];
}

/**
 * Ein Speicherauftrag der Detailansicht — über die Schreib-Warteschlange.
 *
 * Erst bei Ausführung: frische Zeile lesen, abgleichen (Dreiwege für offene
 * Listen, die inzwischen anderswo geschrieben wurden), dann NUR die offenen
 * Listen mit ihrem jetzigen lokalen Stand schreiben. Nach Erfolg gilt eine
 * Liste nur als sauber, wenn ihre Revision noch die gesendete ist — eine
 * neuere Fassung bleibt offen und folgt mit dem nächsten Auftrag. Gemeldet
 * wird die gespeicherte Zeile (Wahrheit der Datenbank, da alle Schreib-
 * vorgänge des Schritts hintereinander laufen); offene Listen übernehmen sie
 * nicht (`uebernimmVonAussen`), ihre Basis steht schon darauf.
 *
 * `null`: nichts offen oder Schritt gelöscht. Ein Schreibfehler wird
 * weitergegeben; die Listen bleiben dann offen.
 */
export const speichereDetail = (d: DetailStand, db: SchrittDb): Promise<DetailSpeicherErgebnis | null> =>
  reiheSchrittAuftrag(d.schrittId, async (): Promise<DetailSpeicherErgebnis | null> => {
    if (!offeneFelder(d).length) return null;
    const zeile = await db.lese(d.schrittId);
    if (!zeile) return null;
    const zusammengefuehrt = gleicheDetailAb(d, listenAus(zeile));
    const felder: Partial<Listen> = {};
    const gesendet: Partial<Record<ListenFeld, number>> = {};
    offeneFelder(d).forEach(f => {
      felder[f] = d.lokal[f];
      gesendet[f] = d.rev[f];
    });
    const antwort = await db.schreibe(d.schrittId, felder);
    if (!antwort) throw new Error('Keine Zeile geändert (fehlende Berechtigung?)');
    const gespeichert = listenAus({ ...zeile, ...felder, ...antwort });
    LISTEN_FELDER.forEach(f => {
      const r = gesendet[f];
      if (r === undefined) return;
      d.gespeichert[f] = Math.max(d.gespeichert[f], r);
      d.basis = { ...d.basis, [f]: gespeichert[f] };
    });
    return { zeile: gespeichert, antwort, zusammengefuehrt };
  });

/** Gemeldete Listen auf die aktuelle Zeile legen — nie eine ganze (ältere) Antwortzeile. */
export const spaltenUebernehmen = (aktuell: Arbeitsschritt, werte: Partial<Listen>): Arbeitsschritt => ({ ...aktuell, ...werte });

/**
 * Dialog „Arbeitsschritt bearbeiten“: nur die im Dialog GEÄNDERTEN Spalten
 * schreiben (Vergleich mit dem Stand beim Öffnen). Geänderte Listen werden
 * dreiwege gegen die frische Zeile zusammengeführt — was die Detailansicht
 * oder die Ergänzung inzwischen gespeichert hat, bleibt.
 */
export const dialogPayload = (
  start: Record<string, unknown>,
  jetzt: Record<string, unknown>,
  zeile: Arbeitsschritt,
): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  Object.keys(jetzt).forEach(k => {
    if (JSON.stringify(jetzt[k]) === JSON.stringify(start[k])) return;
    if ((LISTEN_FELDER as readonly string[]).includes(k)) {
      const f = k as ListenFeld;
      out[k] = dreiwegeListe((start[k] as string[]) || [], (jetzt[k] as string[]) || [], zeile[f] || []);
    } else {
      out[k] = jetzt[k];
    }
  });
  return out;
};
